'use client'

import * as React from 'react'
import { cn } from '../../utils/cn'
import { cva } from 'class-variance-authority'

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2',
    'font-medium rounded-lg',
    'transition-all duration-200',
    'disabled:opacity-50 disabled:cursor-not-allowed',
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-[var(--accent)] text-white',
          'hover:bg-[var(--accent-hover)]',
        ],
        secondary: [
          'bg-[var(--landing-bg-card)] text-[var(--landing-text-primary)]',
          'border border-[var(--landing-border)]',
          'hover:border-[var(--landing-border-strong)] hover:bg-[var(--landing-bg-hover)]',
        ],
        ghost: [
          'bg-transparent text-[var(--landing-text-secondary)]',
          'hover:text-[var(--landing-text-primary)] hover:bg-[var(--landing-bg-hover)]',
        ],
        outline: [
          'bg-transparent text-[var(--landing-text-primary)]',
          'border border-[var(--landing-border)]',
          'hover:border-[var(--landing-border-strong)] hover:bg-[var(--landing-bg-hover)]',
        ],
      },
      size: {
        sm: 'px-3 py-1.5 text-sm',
        md: 'px-4 py-2 text-sm',
        lg: 'px-6 py-3 text-base',
      },
      glow: {
        true: 'cta-glow',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      glow: false,
    },
  }
)

export interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'ghost' | 'outline'
  size?: 'sm' | 'md' | 'lg'
  glow?: boolean
  children: React.ReactNode
  href?: string
  onClick?: () => void
  className?: string
  disabled?: boolean
}

export function Button({
  variant = 'primary',
  size = 'md',
  glow = false,
  children,
  href,
  onClick,
  className,
  disabled
}: ButtonProps) {
  const classes = cn(buttonVariants({ variant, size, glow }), className)

  if (href) {
    const isExternal = href.startsWith('http')
    return (
      <a
        href={href}
        className={classes}
        {...(isExternal && { target: '_blank', rel: 'noopener noreferrer' })}
      >
        {children}
      </a>
    )
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={classes}
    >
      {children}
    </button>
  )
}
